import React, { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { CarOptionProps } from '@components/v2/CarOption/index';
import { useUILoadingOnConfigurator } from '@hooks/useUILoadingOnConfigurator';
import { useGTM } from '@hooks/useGTM';
import { BRAND, isBrandOV, isMarketGB, ModalVersion, ROLES } from 'src/constants/main';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { useJourneyType } from '@hooks/useJourneyType';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useOptionPrice } from '@components/CarOptionsInterior/useOptionPrice';
import useTranslations from '@hooks/useTranslations';
import { theme } from 'src/styles/theme';
import ReactImageFallback from 'react-image-fallback';
import { getAssetUrl } from '@utils/assetUrl.utils';
import { useDispatch } from 'react-redux';
import Checkbox from '@components/Checkbox';
import { useConfiguratorDuck } from '@hooks/useConfiguratorDuck';
import Icon, { Icons } from '@components/Icon';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { Link } from '@components/v2/link';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { useRenderDescription } from '@hooks/useRenderDescription';

export const CAR_OPTION_NAME_CLASS_NAME = 'option-card__name';

export const CarOptionTemplate: FC<CarOptionProps> = ({
    className,
    onChange,
    option,
    onClick,
    sendFullOptionAsParameterOnClick,
}) => {
    const { t } = useTranslations();
    const { pushUserAction } = useGTM();
    const { uiIsLoading, uiLoadingDisabledClass } = useUILoadingOnConfigurator();
    const { isFinance } = useJourneyType();
    const { formatConfiguratorPrice } = useMoneyFormatter();
    const { getPrice } = useOptionPrice();
    const dispatch = useDispatch();
    const { selectedOptions } = useConfiguratorDuck();
    const { renderDescription } = useRenderDescription();

    const [isSelected, setIsSelected] = useState(false);

    const onClickPush = useCallback(() => {
        if (!uiIsLoading) {
            pushUserAction({
                action: 'Choose::Option',
                eventName: 'uaevent',
                label: option.title,
            });
            onChange?.(option);
        }
    }, [uiIsLoading, option, onChange]);

    useEffect(() => {
        setIsSelected(selectedOptions.includes(option.id));
    }, [selectedOptions]);

    const isBudgetChangeAllowed = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BUDGET_CHANGE_ALLOWED);
    const hideCashPayment = !isBudgetChangeAllowed && isFinance;
    const showExtendedPrice = !isMarketGB || (isMarketGB && !hideCashPayment);
    const monthlyPrice = option ? getPrice(option).monthlyPrice : 0;
    const isTiresLabellingEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_TIRES_LABELLING_ENABLED);

    const handleDetailClick = () =>
        dispatch(
            UIDuck.openModal({
                data: {
                    category: option.category.category,
                    image: option.images[0]['url'],
                    description: renderDescription(option?.description),
                    title: option.title,
                    ...(isTiresLabellingEnabled &&
                        option?.tires?.data && {
                            tiresSubtitle: t('technicalFeatures.tiresDetailModal.title'),
                            tires: option.tires.data,
                            tiresDescription: t('technicalFeatures.tiresDetailModal.subtitle'),
                        }),
                },
                modalType: 'CAR_OPTION_DETAIL',
                modalVersion: ModalVersion.v2,
            })
        );

    return (
        <div
            className={`${className} option-card ${uiLoadingDisabledClass} ${isSelected ? 'isSelected' : ''}`}
            key={option.id}
            id={`TESTING_OPTION_${option.id}`}
        >
            <div className="inner">
                <div className={CAR_OPTION_NAME_CLASS_NAME}>
                    <div
                        data-testid={`TESTING_OPTION_NAME_${option.id}`}
                        onClick={() => onClick && onClick(sendFullOptionAsParameterOnClick ? option : option.id)}
                    >
                        <span>
                            <Checkbox
                                className={'checkbox'}
                                onChange={() =>
                                    onClick && onClick(sendFullOptionAsParameterOnClick ? option : option.id)
                                }
                                checked={isSelected}
                                name={`${option.id}-option-checkbox`}
                                dataTestId="TESTING_CONFIRM_CHECKBOX"
                                withoutClick
                            />
                        </span>
                        <span>{option.title}</span>
                    </div>
                    <div className="option-card__add-priceWrapper">
                        <span className="option-card__add-priceWrapper-price" data-testid="TESTING_OPTIONS_PRICE">
                            {showExtendedPrice &&
                                formatConfiguratorPrice(
                                    getPrice(option).totalPrice,
                                    '+ ',
                                    true,
                                    t('configurator.colors.includedInPrice'),
                                    null,
                                    monthlyPrice ? t('configurator.optionPrice.suffix') : ''
                                )}
                            {monthlyPrice && monthlyPrice >= 0
                                ? formatConfiguratorPrice(
                                      monthlyPrice,
                                      '+ ',
                                      true,
                                      t('configurator.colors.includedInPrice'),
                                      null,
                                      `/${t('price.monthly')}`
                                  )
                                : ''}
                        </span>
                    </div>
                </div>
                <div className="option-card__wrap">
                    <div>
                        <>
                            <AccessibleButton
                                className={`option-card__imageWrapper ${
                                    !option.images[0]?.url ? 'withoutOptionIcon' : 'withOptionIcon'
                                }`}
                                tabIndex={0}
                                onClick={handleDetailClick}
                            >
                                <span>
                                    {isTiresLabellingEnabled && option?.tires ? (
                                        <Icon
                                            name={Icons.InfoCircle}
                                            color={theme.colors.primary}
                                            height={20}
                                            width={20}
                                            className="info"
                                            role={ROLES.IMG}
                                            altId="svgImgTyresTechChars"
                                            altText={t('technicalFeatures.tiresDetailModal.title')}
                                        />
                                    ) : (
                                        <Icon
                                            altId="optionIcon"
                                            altText={t('option.detail.shortText.info')}
                                            name={Icons.Search}
                                            className="optionIcon"
                                            height={24}
                                            width={24}
                                        />
                                    )}

                                    <ReactImageFallback
                                        className="option-card__image"
                                        alt={option.title}
                                        src={option.images[0]?.url}
                                        fallbackImage=""
                                    />
                                </span>
                            </AccessibleButton>
                            <p className="option-card__detailDescription">{renderDescription(option?.description)}</p>
                            {option?.description && (
                                <Link
                                    tabIndex={0}
                                    className="link"
                                    isUnderline
                                    txt={t('option.detail.shortText.info')}
                                    onClick={handleDetailClick}
                                >
                                    <AccessibleButton tabIndex={0} onClick={handleDetailClick}>
                                        <p className="option-card__detailLink">{t('option.detail.shortText.info')}</p>
                                    </AccessibleButton>
                                </Link>
                            )}
                        </>
                    </div>
                </div>
            </div>
        </div>
    );
};
